import { Head, router, usePage } from '@inertiajs/react'
import { Settings as SettingsIcon } from 'lucide-react'
import { useEffect } from 'react'
import { toast } from 'sonner'
import MainLayout from '@/components/Layout/MainLayout'
import { Card, CardContent } from '@/components/ui/Card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { BillingCard } from '@/components/settings/BillingCard'
import { ChannelsView } from '@/components/settings/ChannelsView'
import { WidgetSettingsCard } from '@/components/settings/WidgetSettingsCard'
import { WidgetInstallationCard } from '@/components/settings/WidgetInstallationCard'
import { SendToDeveloperCard } from '@/components/settings/SendToDeveloperCard'
import { TopicsSettingsCard } from '@/components/settings/TopicsSettingsCard'
import { InboxesSettingsCard } from '@/components/settings/InboxesSettingsCard'
import { SupportHoursCard } from '@/components/settings/SupportHoursCard'
import { McpApiKeysCard } from '@/components/settings/McpApiKeysCard'
import { TeamSettingsCard } from '@/components/settings/TeamSettingsCard'
import { EmailDomainSettings } from '@/components/settings/EmailDomainSettings'
import { EmailBrandingSettings } from '@/components/settings/EmailBrandingSettings'
import { EmailTemplateTestCard } from '@/components/settings/EmailTemplateTestCard'
import { CannedAnswersList, ClustersList, AiSuggestionsToggle } from '@/components/shortcuts'
import { HelpCenterBrandingCard } from '@/components/settings/HelpCenterBrandingCard'
import { HelpCenterIntegrationsCard } from '@/components/settings/HelpCenterIntegrationsCard'
import { HelpCenterRedirects } from '@/components/help_center/HelpCenterRedirects'
import { HelpSiteDomainSettings } from '@/components/help_center/HelpSiteDomainSettings'
import { SettingsNav } from '@/components/settings/SettingsNav'
import { AccountDetailsCard } from '@/components/settings/AccountDetailsCard'
import { CsatSettingsCard } from '@/components/settings/CsatSettingsCard'
import { ProductsSettingsCard } from '@/components/settings/ProductsSettingsCard'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { SettingsProps } from './Settings.types'

export default function Settings(props: SettingsProps) {
  const {
    current_user, account, widget_script_url, subscription, team, settings_inboxes, settings_topics,
    email_domain, email_branding, email_preview_conversations, canned_answers, canned_answer_clusters,
    ai_suggestions_enabled, timezone_options, mcp_api_keys, new_mcp_key_token, help_center_settings,
    help_site_domain, help_hosted_url, help_web_widgets, help_redirects, products, current_brand, channels,
    channel_type_options, can_manage_products, can_create_products, max_products,
  } = props
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props
  const { withAccount } = useRouteParams()

  const { url } = usePage()
  const search = new URLSearchParams(url.split('?')[1] || '')
  const current = {
    tab: search.get('tab') || 'general',
    brand: search.get('brand'),
    channel: search.get('channel'),
  }
  const helpTab = search.get('help_tab') || 'branding'
  const selectedHelpTab = ['branding', 'domain', 'redirects', 'integrations'].includes(helpTab) ? helpTab : 'branding'
  const selectedChannel = (channels ?? []).find((c) => c.id === current.channel) ?? (channels ?? [])[0] ?? null

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  const handleLogoUpload = (file: File) => {
    const formData = new FormData()
    formData.append('account[logo]', file)
    router.patch(routes.settings.update.path(withAccount()), formData, { forceFormData: true, preserveScroll: true })
  }

  const handleTimezoneChange = (value: string) => {
    router.patch(routes.settings.update.path(withAccount()), { account: { timezone: value } }, { preserveScroll: true })
  }

  const renderContent = () => {
    if (!account) return null
    switch (current.tab) {
      case 'general':
        return (
          <AccountDetailsCard
            account={account}
            timezoneOptions={timezone_options}
            onLogoUpload={handleLogoUpload}
            onTimezoneChange={handleTimezoneChange}
          />
        )
      case 'team':
        return <TeamSettingsCard team={team} />
      case 'products':
        return (
          <ProductsSettingsCard
            products={products}
            can_manage={can_manage_products}
            can_create={can_create_products}
            max_products={max_products}
          />
        )
      case 'channels':
        return (
          <ChannelsView
            channels={channels ?? []}
            channelTypeOptions={channel_type_options ?? []}
            brand={current_brand}
            brands={products ?? []}
          />
        )
      case 'widget':
        return selectedChannel ? (
          <>
            <WidgetSettingsCard channel={selectedChannel} />
            <WidgetInstallationCard channel={selectedChannel} widgetScriptUrl={widget_script_url} />
            <SendToDeveloperCard
              widgetScriptUrl={widget_script_url}
              widgetToken={selectedChannel.widget_token}
              channelId={selectedChannel.id}
            />
            <TopicsSettingsCard channel={selectedChannel} topics={settings_topics} />
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Widget not found.</p>
        )
      case 'help-center':
        return (
          <Tabs value={selectedHelpTab} onValueChange={help_tab =>
            router.get(routes.settings.show.path(withAccount()), { tab: 'help-center', brand: current_brand?.slug, help_tab }, { preserveState: true, preserveScroll: true })
          } className="space-y-6">
            <TabsList>
              <TabsTrigger value="branding" className="cursor-pointer">Branding</TabsTrigger>
              <TabsTrigger value="domain" className="cursor-pointer">Domain</TabsTrigger>
              <TabsTrigger value="redirects" className="cursor-pointer">Redirects</TabsTrigger>
              <TabsTrigger value="integrations" className="cursor-pointer">Integrations</TabsTrigger>
            </TabsList>
            <TabsContent value="branding" className="space-y-6 mt-0">
              {help_center_settings && <HelpCenterBrandingCard settings={help_center_settings} />}
            </TabsContent>
            <TabsContent value="domain" className="space-y-6 mt-0">
              <div className="max-w-3xl"><HelpSiteDomainSettings domain={help_site_domain} hostedUrl={help_hosted_url} /></div>
            </TabsContent>
            <TabsContent value="redirects" className="space-y-6 mt-0">
              {help_redirects ? <HelpCenterRedirects key={help_redirects.brand} {...help_redirects} /> :
                <p className="text-sm text-muted-foreground">Only account owners and admins can manage redirects.</p>}
            </TabsContent>
            <TabsContent value="integrations" className="space-y-6 mt-0">
              {help_center_settings && (
                <HelpCenterIntegrationsCard settings={help_center_settings} webWidgets={help_web_widgets} />
              )}
            </TabsContent>
          </Tabs>
        )
      case 'inboxes':
        return <InboxesSettingsCard inboxes={settings_inboxes} />
      case 'canned-answers':
        return (
          <>
            <CannedAnswersList answers={canned_answers} />
            <ClustersList clusters={canned_answer_clusters} />
            <AiSuggestionsToggle enabled={ai_suggestions_enabled} />
          </>
        )
      case 'email':
        return (
          <>
            <EmailDomainSettings emailDomain={email_domain} />
            <EmailTemplateTestCard conversations={email_preview_conversations} currentUserEmail={current_user.email} />
          </>
        )
      case 'email-branding':
        return email_branding ? <EmailBrandingSettings branding={email_branding} /> : null
      case 'support-hours':
        return (
          <SupportHoursCard
            schedule={account.support_hours}
            timezone={account.timezone}
            firstResponseSlaSeconds={account.first_response_sla_seconds}
          />
        )
      case 'csat':
        return <CsatSettingsCard csat={account.csat} entitled={account.csat_entitled} />
      case 'api-keys':
        return <McpApiKeysCard keys={mcp_api_keys} newKeyToken={new_mcp_key_token} />
      case 'billing':
        return <BillingCard subscription={subscription} billingEmail={account.billing_email} />
      default:
        return null
    }
  }

  return (
    <>
      <Head title="Settings" />
      <MainLayout user={current_user} breadcrumbs={[{ label: 'Settings' }]}>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-8">
              <SettingsIcon className="size-6 text-muted-foreground" />
              <h2 className="text-2xl font-semibold">Account Settings</h2>
            </div>

            {account ? (
              <div className="flex flex-col gap-6 md:flex-row md:gap-8">
                <SettingsNav brands={products ?? []} current={current} />
                <div className="min-w-0 flex-1 space-y-6">{renderContent()}</div>
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">No account found. Please contact support.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </MainLayout>
    </>
  )
}
